import { getProjectId } from 'serviceWorker/db/config';
import type { CollectionRepairReason } from 'serviceWorker/db/collectionRepairReason';
import { logCollectionDownloadedAgain } from 'serviceWorker/helpers/dataLog';
import type { Services } from 'shared/domain/messages/message';

export interface RepairableService {
  /** Marks the collection to be downloaded again in full by its next sync. */
  requestDownloadAgain: () => Promise<void>;
}

/** Collections downloaded again in this run of the worker, by entity and project. */
const repairedCollections = new Set<string>();

/**
 * Marks a collection to be downloaded again in full, at most once per
 * collection and project in a run of the worker: an item that does not exist,
 * or an API that keeps refusing, must not download the collection over and
 * over. Resolves whether it was marked; the caller then syncs it.
 */
export async function requestCollectionDownloadAgain(
  service: RepairableService,
  entityName: Services,
  reason: CollectionRepairReason
): Promise<boolean> {
  const projectId = await getProjectId();
  const key = `${entityName}:${projectId}`;
  if (repairedCollections.has(key)) {
    return false;
  }
  repairedCollections.add(key);
  await service.requestDownloadAgain();
  void logCollectionDownloadedAgain(entityName, reason, projectId);
  return true;
}

/** For tests: every collection may be downloaded again. */
export function forgetCollectionRepairs(): void {
  repairedCollections.clear();
}
